import { API_BASE_URL } from "../config";
import fetchWithAuth from "../middlewares/fetchWithAuth";

const BUTTON_PANELS_URL = `${API_BASE_URL}/buttonPanels`;
const LOCAL_STORAGE_KEY = "soccerAnalytics_buttonPanels";

// --- localStorage fallback ---

function getLocalPanels() {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveLocalPanels(panels) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(panels));
}

// --- API helpers ---

async function parseResponse(response) {
  if (!response) return null;

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return null;
  }

  return response.json();
}

function normalizeArrayPayload(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];

  const candidates = [
    data.items,
    data.buttonPanels,
    data.panels,
    data.data,
    data.results,
    data.value,
  ];

  const firstArray = candidates.find(Array.isArray);
  return Array.isArray(firstArray) ? firstArray : [];
}

function isApiUnavailable(response) {
  return !response || response.status === 404 || response.status === 501;
}

async function handleResponse(response, fallbackMessage, options = {}) {
  const { allowMissingArray = false, allowMissingNull = false } = options;

  if (!response) {
    return allowMissingArray ? [] : null;
  }

  if (response.ok) {
    const data = await parseResponse(response);
    if (allowMissingArray) {
      return normalizeArrayPayload(data);
    }
    return data;
  }

  if ((response.status === 404 || response.status === 501) && allowMissingArray) {
    return [];
  }

  if ((response.status === 404 || response.status === 501) && allowMissingNull) {
    return null;
  }

  let message = fallbackMessage;

  try {
    const payload = await parseResponse(response);
    message = payload?.message || payload?.error || message;
  } catch {
    // Mantener mensaje por defecto.
  }

  const error = new Error(message);
  error.status = response.status;
  throw error;
}

// --- Public API with localStorage fallback ---

export async function getUserPanels(navigate) {
  try {
    const response = await fetchWithAuth(
      BUTTON_PANELS_URL,
      { method: "GET", headers: { "Content-Type": "application/json", "Cache-Control": "no-cache" }, cache: "no-store" },
      navigate
    );

    if (isApiUnavailable(response)) {
      return getLocalPanels();
    }

    if (response.ok) {
      const data = await parseResponse(response);
      const panels = normalizeArrayPayload(data);
      // Sync: merge local panels not yet in API
      const localPanels = getLocalPanels();
      if (localPanels.length > 0 && panels.length === 0) {
        return localPanels;
      }
      return panels;
    }

    const localPanels = getLocalPanels();
    if (localPanels.length) return localPanels;
    return await handleResponse(response, "No se pudieron cargar las botoneras guardadas.");
  } catch (error) {
    const localPanels = getLocalPanels();
    if (localPanels.length) return localPanels;
    throw error;
  }
}

export async function getPanel(panelId, navigate) {
  if (!panelId) return null;

  try {
    const response = await fetchWithAuth(
      `${BUTTON_PANELS_URL}/${encodeURIComponent(String(panelId))}`,
      { method: "GET", headers: { "Content-Type": "application/json", "Cache-Control": "no-cache" }, cache: "no-store" },
      navigate
    );

    if (isApiUnavailable(response)) {
      const locals = getLocalPanels();
      return locals.find((p) => p.id === panelId) || null;
    }

    if (response.ok) {
      return await parseResponse(response);
    }

    return null;
  } catch {
    const locals = getLocalPanels();
    return locals.find((p) => p.id === panelId) || null;
  }
}

export async function createPanel(payload, navigate) {
  try {
    const response = await fetchWithAuth(
      BUTTON_PANELS_URL,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
      navigate
    );

    if (isApiUnavailable(response)) {
      // Fallback: save to localStorage
      const panels = getLocalPanels();
      const newPanel = { ...payload, id: payload.id || `local_${Date.now()}`, _local: true };
      panels.push(newPanel);
      saveLocalPanels(panels);
      return newPanel;
    }

    return handleResponse(response, "Error al crear el panel de botones", {
      allowMissingNull: true,
    });
  } catch {
    // Fallback: save to localStorage
    const panels = getLocalPanels();
    const newPanel = { ...payload, id: payload.id || `local_${Date.now()}`, _local: true };
    panels.push(newPanel);
    saveLocalPanels(panels);
    return newPanel;
  }
}

export async function updatePanel(panelId, payload, navigate) {
  if (!panelId) return null;

  try {
    const response = await fetchWithAuth(
      `${BUTTON_PANELS_URL}/${encodeURIComponent(String(panelId))}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      },
      navigate
    );

    if (isApiUnavailable(response)) {
      // Fallback: update in localStorage
      const panels = getLocalPanels();
      const idx = panels.findIndex((p) => p.id === panelId);
      const updated = { ...payload, id: panelId, _local: true };
      if (idx >= 0) {
        panels[idx] = updated;
      } else {
        panels.push(updated);
      }
      saveLocalPanels(panels);
      return updated;
    }

    return handleResponse(response, "Error al actualizar el panel de botones", {
      allowMissingNull: true,
    });
  } catch {
    const panels = getLocalPanels();
    const idx = panels.findIndex((p) => p.id === panelId);
    const updated = { ...payload, id: panelId, _local: true };
    if (idx >= 0) {
      panels[idx] = updated;
    } else {
      panels.push(updated);
    }
    saveLocalPanels(panels);
    return updated;
  }
}

export async function deletePanel(panelId, navigate) {
  if (!panelId) return null;

  try {
    const response = await fetchWithAuth(
      `${BUTTON_PANELS_URL}/${encodeURIComponent(String(panelId))}`,
      { method: "DELETE", headers: { "Content-Type": "application/json" } },
      navigate
    );

    if (isApiUnavailable(response)) {
      // Fallback: delete from localStorage
      const panels = getLocalPanels();
      const filtered = panels.filter((p) => p.id !== panelId);
      saveLocalPanels(filtered);
      return { message: "Eliminada (local)" };
    }

    return handleResponse(response, "Error al eliminar el panel de botones", {
      allowMissingNull: true,
    });
  } catch {
    const panels = getLocalPanels();
    const filtered = panels.filter((p) => p.id !== panelId);
    saveLocalPanels(filtered);
    return { message: "Eliminada (local)" };
  }
}

const buttonPanelService = {
  getUserPanels,
  getPanel,
  createPanel,
  updatePanel,
  deletePanel,
};

export default buttonPanelService;
